import { zodResolver } from "@hookform/resolvers/zod";
import type { TemplateContext } from "@unbox/api";
import { ChevronDown, Lock01 } from "@untitledui/icons";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod/v3";
import { useMessageCreators } from "@/api/message-creator";
import { useStoreComposerImage } from "@/api/message-images";
import { Button } from "@/components/base/buttons/button";
import { CreatorTag } from "@/components/creator-tag";
import { type ComposerImage, releaseComposerImage } from "@/components/message-composer/composer-image";
import { toastImageUploadFailed } from "@/components/message-composer/composer-toasts";
import { MessageComposerField } from "@/components/message-composer/message-composer-field";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { useMerchantName } from "@/hooks/use-merchant-name";
import { useBulkActionContext } from "@/providers/bulk-action-progress-provider";
import { error, success } from "@/providers/ToastProvider";
import { formatCreatorIdShort } from "@/utils/formatters";
import { pluralize } from "@/utils/text";
import { Tooltip, TooltipTrigger } from "./base/tooltip/tooltip";

// ============================================================================
// TYPES
// ============================================================================

export type CreatorVideo = {
  videoId: string;
  creatorId: string;
  thumbnail: string | null;
};

export type CreatorRecipient = {
  creatorId: string;
  handle: string | null;
  thumbnail: string | null;
};

// ============================================================================
// SHARED COMPONENTS (exported for reuse in action dialogs)
// ============================================================================

export type Creator = {
  creatorId: string;
  handle: string | null;
  thumbnail: string | null;
};

export interface CreatorTagsListProps {
  creators: Creator[];
  isBulk: boolean;
  isPending: boolean;
  onRemove?: (creatorId: string) => void;
  onCreatorClick?: (creatorId: string) => void;
  activeCreatorId?: string;
  scrollRef?: React.RefObject<HTMLDivElement | null>;
  hasOverflow?: boolean;
  forceExpanded?: boolean;
}

const COLLAPSED_VISIBLE_COUNT = 2;

export const CreatorTagsList = ({
  creators,
  isBulk,
  isPending,
  onRemove,
  onCreatorClick,
  activeCreatorId,
  scrollRef,
  hasOverflow,
  forceExpanded,
}: CreatorTagsListProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (forceExpanded) {
      setIsExpanded(true);
    }
  }, [forceExpanded]);

  if (!isBulk) {
    return (
      <div className="flex flex-col gap-2">
        <div className="text-sm font-medium">To</div>
        <div className="flex flex-wrap gap-2">
          <CreatorTag
            creatorId={creators[0].creatorId}
            handle={creators[0].handle}
            thumbnail={creators[0].thumbnail}
            disabled={isPending}
          />
        </div>
      </div>
    );
  }

  const canCollapse = creators.length > COLLAPSED_VISIBLE_COUNT;
  const hiddenCount = creators.length - COLLAPSED_VISIBLE_COUNT;

  if (canCollapse && !isExpanded) {
    const visibleCreators = creators.slice(0, COLLAPSED_VISIBLE_COUNT);
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-sm font-medium shrink-0">To</div>
        {visibleCreators.map((creator) => (
          <CreatorTag
            key={creator.creatorId}
            creatorId={creator.creatorId}
            handle={creator.handle}
            thumbnail={creator.thumbnail}
            onRemove={onRemove ? () => onRemove(creator.creatorId) : undefined}
            disabled={isPending}
          />
        ))}
        <button
          type="button"
          onClick={() => setIsExpanded(true)}
          className="text-sm font-medium text-secondary whitespace-nowrap flex items-center gap-0.5 shrink-0 ml-auto"
        >
          +{hiddenCount} more
          <ChevronDown className="size-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 relative">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium">To</div>
        {canCollapse && (
          <button
            type="button"
            onClick={() => setIsExpanded(false)}
            className="text-sm font-medium text-secondary flex items-center gap-0.5"
          >
            Show less
            <ChevronDown className="size-4 rotate-180" />
          </button>
        )}
      </div>
      <div ref={scrollRef} className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
        {creators.map((creator) => (
          <CreatorTag
            key={creator.creatorId}
            creatorId={creator.creatorId}
            handle={creator.handle}
            thumbnail={creator.thumbnail}
            onRemove={onRemove ? () => onRemove(creator.creatorId) : undefined}
            onClick={onCreatorClick ? () => onCreatorClick(creator.creatorId) : undefined}
            active={activeCreatorId === creator.creatorId}
            disabled={isPending}
          />
        ))}
      </div>
      {hasOverflow && (
        <div className="absolute bottom-0 left-0 right-0 h-[3.75rem] bg-gradient-to-t from-white to-transparent pointer-events-none rounded-b-md" />
      )}
    </div>
  );
};

export interface SecurityInfoBoxProps {
  isBulk: boolean;
}

export const SecurityInfoBox = () => (
  <Tooltip title={"Messages are sent securely through TikTok. Once sent, this message can't be edited or unsent."}>
    <TooltipTrigger className="flex flex-row items-center gap-2 text-muted-foreground text-sm">
      <Lock01 className="size-4 shrink-0" />
      Private & secure
    </TooltipTrigger>
  </Tooltip>
);

interface FormErrorsProps {
  errors: {
    creatorIds?: { message?: string };
    root?: { message?: string };
  };
}

const FormErrors = ({ errors }: FormErrorsProps) => (
  <>
    {errors.creatorIds && <p className="text-sm text-destructive">{errors.creatorIds.message}</p>}
    {errors.root && <p className="text-sm text-destructive">{errors.root.message}</p>}
  </>
);

// ============================================================================
// CREATOR-DIRECT MESSAGING: MessageCreatorsDialog
// Handles both single and bulk creator-direct messaging
// ============================================================================

interface MessageCreatorsDialogProps {
  recipients: CreatorRecipient[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

const creatorFormSchema = z.object({
  message: z.string(),
  creatorIds: z.array(z.string()).min(1, "At least one creator must be selected"),
});

export const MessageCreatorsDialog = ({ recipients, open, onOpenChange, onSuccess }: MessageCreatorsDialogProps) => {
  const [hasOverflow, setHasOverflow] = useState(false);
  const [previewCreatorId, setPreviewCreatorId] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [image, setImage] = useState<ComposerImage | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const merchantName = useMerchantName();

  const isBulk = recipients.length > 1;

  const form = useForm({
    resolver: zodResolver(creatorFormSchema),
    defaultValues: { message: "", creatorIds: [] as string[] },
  });

  const { mutateAsync, isPending: isSending } = useMessageCreators();
  const { store: storeImage, isPending: isStoringImage } = useStoreComposerImage();
  const isPending = isSending || isStoringImage;
  const { startTracking: startBulkTracking } = useBulkActionContext();

  // Get unique creators from recipients
  const uniqueCreators = recipients.reduce((acc, recipient) => {
    if (!acc.has(recipient.creatorId)) {
      acc.set(recipient.creatorId, recipient);
    }
    return acc;
  }, new Map<string, CreatorRecipient>());

  // Initialize form when dialog opens
  useEffect(() => {
    if (open) {
      form.reset({
        message: "",
        creatorIds: Array.from(uniqueCreators.keys()),
      });
      setImage((current) => {
        releaseComposerImage(current);
        return null;
      });
    }
  }, [open]);

  const selectedCreatorIds = form.watch("creatorIds");
  const selectedCreators = Array.from(uniqueCreators.values()).filter((c) => selectedCreatorIds.includes(c.creatorId));

  // Check for overflow whenever selected creators change
  useEffect(() => {
    const checkOverflow = () => {
      if (scrollContainerRef.current) {
        const hasScrollableContent = scrollContainerRef.current.scrollHeight > scrollContainerRef.current.clientHeight;
        setHasOverflow(hasScrollableContent);
      }
    };

    // Use setTimeout to ensure DOM is fully rendered
    const timeoutId = setTimeout(checkOverflow, 0);

    // Also check on window resize
    window.addEventListener("resize", checkOverflow);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", checkOverflow);
    };
  }, [selectedCreators]);

  const previewCreator = selectedCreators.find((c) => c.creatorId === previewCreatorId) ?? selectedCreators[0];

  const previewContext: TemplateContext | undefined = previewCreator
    ? {
        creatorName: previewCreator.handle ?? formatCreatorIdShort(previewCreator.creatorId),
        shopName: merchantName ?? "",
      }
    : undefined;

  const removeCreator = (creatorId: string) => {
    const current = form.getValues("creatorIds");
    form.setValue(
      "creatorIds",
      current.filter((id) => id !== creatorId),
    );
  };

  const onSubmit = async (payload: z.infer<typeof creatorFormSchema>) => {
    if (!payload.message.trim() && !image) {
      form.setError("message", { message: "Write a message or add an image" });
      return;
    }
    let imageId: string | null;
    try {
      imageId = await storeImage(image);
    } catch {
      toastImageUploadFailed();
      return;
    }
    const response = await mutateAsync({
      creatorIds: payload.creatorIds,
      message: payload.message.trim(),
      imageId: imageId ?? undefined,
    });

    if (response.async) {
      startBulkTracking(response.jobId, "message_creator", null);
      onSuccess?.();
      onOpenChange(false);
      return;
    }

    // Backend returns: { total, successful, failed, results: [{ creatorId, success }] }
    const { successful, failed, results } = response;

    if (successful > 0) {
      // Get creators that had successful messages
      const successfulCreatorIds = new Set(results.filter((r) => r.success).map((r) => r.creatorId));
      const successfulCreators = selectedCreators.filter((c) => successfulCreatorIds.has(c.creatorId));

      const creatorMessage =
        successfulCreators.length > 3
          ? `${successfulCreators.length} creators`
          : successfulCreators.map((c) => c.handle ?? formatCreatorIdShort(c.creatorId)).join(", ");

      success(`This message will be sent to ${creatorMessage}`);
    }

    if (failed > 0) {
      // Get creators that had all messages fail
      const failedCreatorIds = new Set(results.filter((r) => !r.success).map((r) => r.creatorId));
      const failedCreators = selectedCreators.filter((c) => failedCreatorIds.has(c.creatorId));

      if (failedCreators.length > 0) {
        const usernames = failedCreators.map((c) => c.handle ?? formatCreatorIdShort(c.creatorId));
        error(
          `Message failed to send to ${pluralize(failedCreators.length, "creator", "creators")}. This may be due to TikTok's 5 message limit.`,
          {
            confirmLabel: "Copy Creators",
            onConfirm: () => navigator.clipboard.writeText(usernames.join(", ")),
          },
        );
      }
    }

    // Only close and reset if all succeeded
    if (failed === 0) {
      onSuccess?.();
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px]">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <DialogHeader>
              <DialogTitle>Message creator{isBulk ? "s" : ""} on TikTok</DialogTitle>
              {!isBulk && <DialogDescription>Send a message to this creator through TikTok Shop.</DialogDescription>}
            </DialogHeader>

            <div className="flex flex-col gap-6">
              <CreatorTagsList
                creators={selectedCreators}
                isBulk={isBulk}
                isPending={isPending}
                onRemove={removeCreator}
                onCreatorClick={isBulk ? setPreviewCreatorId : undefined}
                activeCreatorId={showPreview ? previewCreator?.creatorId : undefined}
                scrollRef={scrollContainerRef}
                hasOverflow={hasOverflow}
                forceExpanded={showPreview}
              />

              <MessageComposerField
                control={form.control}
                name="message"
                image={image}
                onImageChange={setImage}
                disabled={isPending}
                variableContext="creator-messaging"
                previewContext={previewContext}
                onPreviewChange={setShowPreview}
                autoFocus
              />

              <FormErrors errors={form.formState.errors} />
            </div>

            <DialogFooter className="items-center sm:justify-between">
              <SecurityInfoBox />
              <div className="flex gap-2">
                <DialogClose asChild>
                  <Button type="button" color="secondary" disabled={isPending}>
                    Cancel
                  </Button>
                </DialogClose>
                <Button isDisabled={isPending} isLoading={isPending} type="submit">
                  Send on TikTok
                </Button>
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
